import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { CalendarClock, ChevronLeft, ChevronRight, ChevronDown, MapPin, ShieldCheck, ShieldAlert, ShieldX, Loader2, RefreshCcw, Printer } from "lucide-react";
import { toast } from "sonner";
import { isLeader } from "@/lib/permissions";

/** Team → Shift log — first clock-in per direct report / L2 report / dept peer
 * for a given MST day. Shows city, geofence status, distance from city center,
 * timestamp, aircraft + task type. Leadership-only route.
 */
export default function ShiftLog() {
  const { user } = useAuth();
  const [date, setDate] = useState(() => {
    // Default to today in MST
    const now = new Date();
    const mst = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return mst.toISOString().slice(0, 10);
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  // Collapsed city sections — set of city codes the user has folded away.
  const [collapsed, setCollapsed] = useState(() => new Set());

  const toggleCity = (code) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/team/shift-log?date=${date}`);
      setRows(data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load shift log");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const shiftDay = (delta) => {
    const d = new Date(date + "T12:00:00");
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const bag = [
        r.user.display_name, r.user.employee_number, r.user.department,
        r.city?.code, r.city?.name,
        r.first_session?.aircraft_tail, r.first_session?.task_type_name,
      ].filter(Boolean).join(" ").toLowerCase();
      return bag.includes(q);
    });
  }, [rows, query]);

  // Group filtered rows by the user's city — sections render as clickable,
  // collapsible headers inside the table.
  const cityGroups = useMemo(() => {
    const by = new Map();
    for (const r of filtered) {
      const key = r.city?.code || "no-city";
      if (!by.has(key)) {
        by.set(key, {
          code: key,
          label: r.city?.code ? `${r.city.code}${r.city.name ? " · " + r.city.name : ""}` : "No city set",
          rows: [],
        });
      }
      by.get(key).rows.push(r);
    }
    return Array.from(by.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { total: rows.length, inside: 0, outside: 0, denied: 0, unavailable: 0, unsupported: 0, notConfigured: 0, noSession: 0, flaggedAdditional: 0 };
    rows.forEach((r) => {
      const s = r.geofence?.status || "no-session";
      if (s === "inside") c.inside++;
      else if (s === "outside") c.outside++;
      else if (s === "denied") c.denied++;
      else if (s === "unavailable") c.unavailable++;
      else if (s === "unsupported") c.unsupported++;
      else if (s === "not-configured") c.notConfigured++;
      else c.noSession++;
      // Any additional flagged entry (clock_stop out-of-radius or a later
      // clock-in beyond the first) also counts toward the Outside tile so
      // the counter matches the rose-tinted rows visible in the table.
      c.flaggedAdditional += (r.additional_entries || []).length;
    });
    return c;
  }, [rows]);

  if (!isLeader(user) && user?.role !== "crew_chief") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <ShieldAlert className="w-8 h-8 mx-auto text-gray-400" />
        <h1 className="mt-3 font-display text-2xl font-semibold">Leadership only</h1>
        <p className="text-sm text-gray-500 mt-1">The shift log is available to Supervisors, Managers, Senior Managers, and Admins.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Team · Shift Log · MST</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1">Who&apos;s on duty — and where.</h1>
          <p className="text-sm text-gray-500 mt-1">Start &amp; stop duty stamps plus out-of-range aircraft clock-ins for every direct report + department peer, cross-checked against their city geofence.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shiftDay(-1)} data-testid="shift-log-prev"><ChevronLeft className="w-4 h-4"/></Button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-1.5 border border-summit-hairline rounded-md text-sm font-mono"
            data-testid="shift-log-date"
          />
          <Button variant="outline" size="icon" onClick={() => shiftDay(1)} data-testid="shift-log-next"><ChevronRight className="w-4 h-4"/></Button>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="shift-log-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5"/> : <RefreshCcw className="w-4 h-4 mr-1.5"/>}
            Refresh
          </Button>
          <Link to="/team/shift-log/report">
            <Button className="bg-summit-primary hover:bg-summit-primary-soft text-white" data-testid="shift-log-print-report">
              <Printer className="w-4 h-4 mr-1.5" /> Print report
            </Button>
          </Link>
        </div>
      </div>

      {/* Roll-up counts */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="shift-log-rollup">
        <RollupTile label="Inside geofence" value={counts.inside} tone="emerald" icon={ShieldCheck} />
        <RollupTile label="Outside / flagged" value={counts.outside + counts.denied + counts.unavailable + counts.unsupported + counts.flaggedAdditional} tone="amber" icon={ShieldAlert} />
        <RollupTile label="Not yet on duty" value={counts.noSession} tone="slate" icon={CalendarClock} />
        <RollupTile label="Total reports" value={counts.total} tone="summit" icon={MapPin} />
      </div>

      <div className="mt-6">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter by name, department, city, aircraft…"
          className="max-w-md"
          data-testid="shift-log-filter"
        />
      </div>

      <div className="mt-4 border border-summit-hairline rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="shift-log-table">
          <thead>
            <tr className="text-left text-gray-500 border-b border-summit-hairline bg-summit-glacier/40">
              <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">User</th>
              <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Duty start (MST)</th>
              <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">City</th>
              <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Distance</th>
              <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">GPS</th>
              <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Aircraft</th>
              <th className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest">Task type</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-400 text-sm"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />Loading shift log…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-400 text-sm">No reports match the filter, or no direct reports for this day.</td></tr>
            )}
            {!loading && cityGroups.map((g) => {
              const isCollapsed = collapsed.has(g.code);
              const clockedIn = g.rows.filter((r) => r.first_session).length;
              return (
                <React.Fragment key={g.code}>
                  <tr
                    className="border-b border-summit-hairline bg-summit-glacier/60 cursor-pointer hover:bg-summit-glacier transition-colors select-none"
                    onClick={() => toggleCity(g.code)}
                    data-testid={`shift-city-header-${g.code}`}
                  >
                    <td colSpan="7" className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                        <MapPin className="w-3 h-3 text-summit-primary" />
                        <span className="font-mono text-[11px] uppercase tracking-widest font-semibold">{g.label}</span>
                        <span className="font-mono text-[10px] text-gray-500">
                          {g.rows.length} {g.rows.length === 1 ? "user" : "users"} · {clockedIn} on duty
                        </span>
                      </div>
                    </td>
                  </tr>
                  {!isCollapsed && g.rows.map((r) => (
                    <React.Fragment key={r.user.id}>
                      <ShiftRow row={r} />
                      {(r.additional_entries || []).map((e) => (
                        <FlaggedRow key={e.id} entry={e} user={r.user} city={r.city} />
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RollupTile({ label, value, tone, icon: Icon }) {
  const toneCls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    summit: "bg-summit-glacier border-summit-hairline text-summit-primary",
  }[tone];
  return (
    <div className={`border rounded-lg px-4 py-3 ${toneCls}`}>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest opacity-70">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="mt-1 font-display text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function ShiftRow({ row }) {
  const s = row.first_session;
  const g = row.geofence || {};
  const city = row.city || {};
  const isDuty = s?.kind === "duty";

  const timeCell = s
    ? new Date(s.started_at_mst || s.started_at_iso).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit", timeZone: "America/Denver" })
    : "—";

  let gpsBadge;
  if (g.status === "inside") gpsBadge = { label: "Inside", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: ShieldCheck };
  else if (g.status === "outside") gpsBadge = { label: "Outside", cls: "bg-rose-100 text-rose-800 border-rose-200", Icon: ShieldAlert };
  else if (g.status === "denied") gpsBadge = { label: "Denied", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: ShieldX };
  else if (g.status === "unavailable") gpsBadge = { label: "Unavailable", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: ShieldX };
  else if (g.status === "unsupported") gpsBadge = { label: "Unsupported", cls: "bg-slate-100 text-slate-700 border-slate-200", Icon: ShieldX };
  else if (g.status === "not-configured") gpsBadge = { label: "N/A", cls: "bg-slate-100 text-slate-500 border-slate-200", Icon: MapPin };
  else gpsBadge = { label: "—", cls: "bg-gray-100 text-gray-400 border-gray-200", Icon: CalendarClock };

  const distanceCell = (() => {
    if (!s) return "—";
    if (g.distance_km == null) return "—";
    const km = g.distance_km;
    const rad = city.radius_km || 1;
    const insideOfRadius = km <= rad;
    return (
      <span className={`font-mono tabular-nums text-xs ${insideOfRadius ? "text-emerald-700" : "text-rose-700"}`}>
        {km.toFixed(km >= 10 ? 1 : 2)} km <span className="text-gray-400">/ {rad} km</span>
      </span>
    );
  })();

  return (
    <tr className="border-b border-summit-hairline last:border-0" data-testid={`shift-row-${row.user.employee_number}`}>
      <td className="px-4 py-2.5">
        <div className="font-medium">{row.user.display_name}</div>
        <div className="text-[11px] text-gray-500 font-mono">#{row.user.employee_number} · {row.user.job_title || "—"}</div>
      </td>
      <td className="px-4 py-2.5">
        <div className="font-mono tabular-nums" data-testid={`shift-time-${row.user.employee_number}`}>
          {timeCell}
        </div>
        {s && s.started_at_iso && (
          <div className="text-[10px] text-gray-400 font-mono">
            {new Date(s.started_at_iso).toLocaleDateString("en-CA", { timeZone: "America/Denver" })}
          </div>
        )}
      </td>
      <td className="px-4 py-2.5">
        {city.code ? (
          <div>
            <div className="font-mono text-xs font-medium">{city.code}</div>
            <div className="text-[10px] text-gray-500">{city.name || ""}</div>
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-2.5" data-testid={`shift-distance-${row.user.employee_number}`}>
        {distanceCell}
      </td>
      <td className="px-4 py-2.5">
        <Badge variant="outline" className={`font-mono text-[10px] uppercase tracking-widest border ${gpsBadge.cls}`} data-testid={`shift-gps-${row.user.employee_number}`}>
          <gpsBadge.Icon className="w-3 h-3 mr-1" /> {gpsBadge.label}
        </Badge>
      </td>
      <td className="px-4 py-2.5">
        {isDuty ? (
          <div>
            <div className="font-mono text-xs font-medium text-emerald-700">DUTY</div>
            <div className="text-[10px] text-gray-500">on-shift</div>
          </div>
        ) : s?.is_whole_day ? (
          <div>
            <div className="font-mono text-xs font-medium text-summit-primary">WHOLE DAY</div>
            <div className="text-[10px] text-gray-500">Admin / Training</div>
          </div>
        ) : s?.aircraft_tail ? (
          <div>
            <div className="font-mono text-xs font-medium">{s.aircraft_tail}</div>
            <div className="text-[10px] text-gray-500">{s.fleet || ""}</div>
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-2.5">
        {isDuty ? (
          <div>
            <div className="text-xs">Start of duty</div>
            {s.auto_closed && <Badge className="mt-1 bg-slate-100 text-slate-700 text-[9px]">Auto-closed</Badge>}
          </div>
        ) : s?.is_whole_day ? (
          <div>
            <div className="text-xs">Admin / Training (whole day)</div>
            {s.is_ot && <Badge className="mt-1 bg-amber-100 text-amber-800 text-[9px]">OT</Badge>}
          </div>
        ) : s?.task_type_name ? (
          <div>
            <div className="text-xs">{s.task_type_name}</div>
            {s.is_capex && s.capex_code_name && (
              <div className="text-[10px] text-emerald-700 mt-0.5 font-mono">AV: {s.capex_code_name}</div>
            )}
            {s.is_ot && <Badge className="mt-1 bg-amber-100 text-amber-800 text-[9px]">OT</Badge>}
            {s.is_work_on_stat && <Badge className="mt-1 bg-rose-100 text-rose-800 text-[9px]">STAT</Badge>}
          </div>
        ) : (
          <span className="text-gray-400 text-xs">{s ? "—" : "not on duty"}</span>
        )}
      </td>
    </tr>
  );
}

function FlaggedRow({ entry, user, city }) {
  const timeCell = new Date(entry.started_at_mst || entry.started_at_iso).toLocaleTimeString(
    "en-CA", { hour: "2-digit", minute: "2-digit", timeZone: "America/Denver" }
  );
  const rad = city.radius_km || 1;
  const km = entry.geofence_distance_km;
  const isDuty = entry.kind === "duty";
  const isDutyStop = entry.kind === "duty_stop";
  const isStop = entry.kind === "clock_stop";
  const isPing = entry.kind === "ping";
  // Duty stop = neutral end-of-shift stamp; extra duty starts = neutral;
  // everything else is a flagged out-of-radius audit row.
  const neutral = isDutyStop || (isDuty && entry.geofence_status !== "outside");
  const rowLabel = isDutyStop ? "Stop duty · end of shift"
    : isDuty ? "Duty start"
    : isStop ? "Clock-out · out-of-radius"
    : isPing ? "30-min ping · out-of-radius"
    : "Clock-in · out-of-radius";
  return (
    <tr
      className={`border-b border-summit-hairline last:border-0 ${neutral ? "bg-indigo-50/40" : "bg-rose-50/40"}`}
      data-testid={`shift-row-flagged-${user.employee_number}-${entry.id}`}
    >
      <td className="px-4 py-2 pl-8">
        <div className={`text-[11px] font-mono uppercase tracking-widest ${neutral ? "text-indigo-700" : "text-rose-700"}`}>↳ {rowLabel}</div>
      </td>
      <td className="px-4 py-2">
        <div className="font-mono tabular-nums text-xs">{timeCell}</div>
      </td>
      <td className="px-4 py-2">
        {city.code ? (
          <span className="font-mono text-[11px]">{city.code}</span>
        ) : <span className="text-gray-400">—</span>}
      </td>
      <td className="px-4 py-2">
        {km != null ? (
          <span className={`font-mono tabular-nums text-xs ${neutral ? "text-gray-600" : "text-rose-700"}`}>
            {km.toFixed(km >= 10 ? 1 : 2)} km <span className="text-gray-400">/ {rad} km</span>
          </span>
        ) : "—"}
      </td>
      <td className="px-4 py-2">
        {neutral ? (
          <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest bg-indigo-100 text-indigo-800 border-indigo-200">
            <CalendarClock className="w-3 h-3 mr-1" /> Stamp
          </Badge>
        ) : (
          <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest bg-rose-100 text-rose-800 border-rose-200">
            <ShieldAlert className="w-3 h-3 mr-1" /> Outside
          </Badge>
        )}
      </td>
      <td className="px-4 py-2">
        {isDuty || isDutyStop ? (
          <span className="font-mono text-[11px] text-emerald-700">DUTY</span>
        ) : entry.aircraft_tail ? (
          <span className="font-mono text-xs">{entry.aircraft_tail}</span>
        ) : "—"}
      </td>
      <td className="px-4 py-2 text-xs">
        {isDutyStop ? (
          <span>
            End of shift
            {entry.auto_closed && <Badge className="ml-1.5 bg-slate-100 text-slate-700 text-[9px]">Auto-closed</Badge>}
          </span>
        ) : isStop ? "Clock-out" : isPing ? "Location ping" : isDuty ? "Start of duty" : (entry.task_type_name || "—")}
      </td>
    </tr>
  );
}
